import { Badge } from './badge';
import { cn } from '../../lib/utils';

/**
 * Single source of truth for rendering a status as a coloured chip.
 *
 * Every page used to map `order.status` / `supplier.verificationStatus` / etc.
 * to a Badge variant inline, so the same status could look different across
 * screens. Use <StatusBadge status={...} /> everywhere instead.
 */

type Variant =
  | 'default' | 'secondary' | 'destructive' | 'success' | 'warning' | 'info'
  | 'pending' | 'confirmed' | 'processing' | 'ready' | 'transit'
  | 'delivered' | 'completed' | 'cancelled' | 'rejected' | 'issue' | 'outline';

type Cfg = { variant: Variant; label?: string };

// Normalised status key -> chip config. Keys are lower-cased + trimmed.
const STATUS_MAP: Record<string, Cfg> = {
  // order lifecycle
  pending: { variant: 'pending' },
  pending_payment: { variant: 'pending', label: 'Pending Payment' },
  awaiting_payment: { variant: 'pending', label: 'Awaiting Payment' },
  confirmed: { variant: 'confirmed' },
  accepted: { variant: 'confirmed' },
  processing: { variant: 'processing' },
  ready_for_pickup: { variant: 'ready', label: 'Ready for Pickup' },
  ready: { variant: 'ready' },
  assigned: { variant: 'info' },
  picked_up: { variant: 'transit', label: 'Picked Up' },
  in_transit: { variant: 'transit', label: 'In Transit' },
  shipped: { variant: 'transit' },
  out_for_delivery: { variant: 'transit', label: 'Out for Delivery' },
  delivered: { variant: 'delivered' },
  completed: { variant: 'completed' },
  cancelled: { variant: 'cancelled' },
  canceled: { variant: 'cancelled', label: 'Cancelled' },
  rejected: { variant: 'rejected' },
  refunded: { variant: 'info' },
  returned: { variant: 'warning' },
  issue_reported: { variant: 'issue', label: 'Issue Reported' },
  on_hold: { variant: 'warning', label: 'On Hold' },
  expired: { variant: 'secondary' },

  // verification / account
  active: { variant: 'success' },
  inactive: { variant: 'secondary' },
  verified: { variant: 'success' },
  unverified: { variant: 'warning' },
  pending_verification: { variant: 'warning', label: 'Pending Verification' },
  approved: { variant: 'success' },
  suspended: { variant: 'destructive' },
  blocked: { variant: 'destructive' },
  banned: { variant: 'destructive' },
  draft: { variant: 'secondary' },

  // payment / payout / withdrawal
  paid: { variant: 'success' },
  unpaid: { variant: 'warning' },
  partial: { variant: 'warning' },
  partially_paid: { variant: 'warning', label: 'Partially Paid' },
  failed: { variant: 'destructive' },
  success: { variant: 'success' },
  initiated: { variant: 'info' },
  requested: { variant: 'pending' },
  payout_pending: { variant: 'pending', label: 'Payout Pending' },

  // generic booleans-as-status
  yes: { variant: 'success' },
  no: { variant: 'secondary' },
  enabled: { variant: 'success' },
  disabled: { variant: 'secondary' },
};

function titleCase(s: string) {
  return s
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export interface StatusBadgeProps {
  status?: string | null;
  /** Override the displayed label. */
  label?: string;
  className?: string;
}

export function StatusBadge({ status, label, className }: StatusBadgeProps) {
  const key = (status || '').toString().toLowerCase().trim();
  if (!key) return <Badge variant="secondary" className={className}>—</Badge>;
  const cfg = STATUS_MAP[key] ?? { variant: 'secondary' as Variant };
  return (
    <Badge variant={cfg.variant} className={cn(className)}>
      {label ?? cfg.label ?? titleCase(key)}
    </Badge>
  );
}

export default StatusBadge;
